import { useEffect, useState } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { useNavigate } from 'react-router'
import { navigationRoutes } from '@/Shared/ui/routers/data/navigationRoutes'
import { array, number, object, ObjectSchema, string, boolean } from 'yup'
import {
    HelpCenterRepository,
    CaseReasonDetails,
    TicketModel,
    CaseFiles,
    CaseProduct,
    SaveCaseRequest,
    TicketHistoryItem,
    TicketDetail,
    SaveCaseReopenRequest
} from '@/HelpCenter/domain'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useLoader } from '@/Shared/ui/loader'
import { useNotification } from '@/Shared/ui/notifications'
import { MetaStatusCodes, NotificationSeverity } from '@/Shared/domain'
import { HelpCenterTranslations } from '@/HelpCenter/ui/helpCenterTranslations'
import { emailSchemaHelpCenter } from '@/Shared/ui/form/validations'
import { useUserData } from '@/User/ui/context/UserDataProvider'
import { UploadedFile } from '@edenredespana/oreneta'
import { useWebTagPermissions } from '@/User/context'
import { WebTagPermissionEnum } from '@/Shared/domain'

export interface HelpCenterFormValues {
    caseReasonId: number
    caseReasonDetailId: number
    caseProductId: number
    subject: string
    description: string
    otherEmail: string
    otherPhone: string
    caseFiles: CaseFiles[]
    acceptPrivacyPolicy: boolean
}

export interface HelpCenterReopenFormValues {
    subject: string
    description: string
    expandedDescription: string
    otherEmail: string
    otherPhone: string
    caseFiles: CaseFiles[]
    acceptPrivacyPolicy: boolean
}

const helpCenterFormSchema = (t: TFunction): ObjectSchema<HelpCenterFormValues> =>
    object({
        caseReasonId: number()
            .required(t(formTranslation.errors.fieldRequired))
            .min(1, t(formTranslation.errors.fieldRequired)),
        caseReasonDetailId: number()
            .required(t(formTranslation.errors.fieldRequired))
            .min(1, t(formTranslation.errors.fieldRequired)),
        caseProductId: number()
            .required(t(formTranslation.errors.fieldRequired))
            .min(1, t(formTranslation.errors.fieldRequired)),
        subject: string()
            .required(t(formTranslation.errors.fieldRequired))
            .max(100, t(HelpCenterTranslations.step4.subject.maxChars)),
        description: string()
            .required(t(formTranslation.errors.fieldRequired))
            .max(1000, t(HelpCenterTranslations.step4.description.maxChars)),
        otherEmail:
            emailSchemaHelpCenter(t, false)
                .default(''),
        otherPhone: string()
            .default('')
            .max(9, t(HelpCenterTranslations.step4.form.maxPhoneChars))
            .test('must-comply-format', t(HelpCenterTranslations.step4.form.invalidFormat), value => {
                if (value !== '') {
                    return /^(6|7)[0-9]{8}$/.test(value ?? '')
                }
                return true
            }),
        caseFiles: array().of(
            object({
                name: string().required(),
                file: string().required(),
            })
        ).defined().default([]),
        acceptPrivacyPolicy: boolean()
            .required(t(HelpCenterTranslations.step4.form.acceptPrivacyPolicy))
            .oneOf([true], t(HelpCenterTranslations.step4.form.acceptPrivacyPolicy)),
    })

const helpCenterReopenFormSchema = (t: TFunction) =>
    object({
        subject: string()
            .required(t(formTranslation.errors.fieldRequired))
            .max(100, t(HelpCenterTranslations.step4.subject.maxChars)),
        description: string()
            .required(t(formTranslation.errors.fieldRequired))
            .max(1000, t(HelpCenterTranslations.step4.description.maxChars)),
        expandedDescription: string()
            .required(t(formTranslation.errors.fieldRequired))
            .max(1000, t(HelpCenterTranslations.step4.description.maxChars)),
        otherEmail:
            emailSchemaHelpCenter(t, false)
                .default(''),
        otherPhone: string()
            .default('')
            .max(9, t(HelpCenterTranslations.step4.form.maxPhoneChars))
            .test('must-comply-format', t(HelpCenterTranslations.step4.form.invalidFormat), value => {
                if (value !== '') {
                    return /^(6|7)[0-9]{8}$/.test(value ?? '')
                }
                return true
            }),
        caseFiles: array().of(
            object({
                name: string().required(),
                file: string().required(),
            })
        ).defined().default([]),
        acceptPrivacyPolicy: boolean()
            .required(t(HelpCenterTranslations.step4.form.acceptPrivacyPolicy))
            .oneOf([true], t(HelpCenterTranslations.step4.form.acceptPrivacyPolicy)),
    })
export interface IUseHelpCenterController {
    getCaseReasons: () => Promise<void>
    caseReasons: TicketModel[]
    selectedReasonDetails: CaseReasonDetails[]
    step: number
    setStep: (step: number) => void
    form: UseFormReturn<HelpCenterFormValues>
    isNextEnabled: boolean
    handleNextStep: () => void
    handlePreviousStep: () => void
    caseProducts: CaseProduct[]
    getCaseProducts: () => Promise<void>
    saveCaseRequest: () => Promise<void>
    isModalOpen: boolean
    setIsModalOpen: (isOpen: boolean) => void
    setHasFileError: (hasError: boolean) => void
    isInvoiceModalOpen: boolean
    setIsInvoiceModalOpen: (isOpen: boolean) => void
    isBankAccountModalOpen: boolean
    setIsBankAccountModalOpen: (isOpen: boolean) => void
    isTPVModalOpen: boolean
    setIsTPVModalOpen: (isOpen: boolean) => void
    isMaterialModalOpen: boolean
    setIsMaterialModalOpen: (isOpen: boolean) => void
    uploadedFiles: UploadedFile[]
    setUploadedFiles: (files: UploadedFile[]) => void
    ticketHistory: TicketHistoryItem[]
    getTicketHistory: () => Promise<void>
    getTicketDetail: (ticketId: string) => Promise<void>
    ticketDetail: TicketDetail | null
    reopenForm: UseFormReturn<HelpCenterReopenFormValues>
    saveTicketReopen: () => Promise<void>
    isReopenFormValid: boolean
    handleReopenCancel: () => void
    isReopenCancelModalOpen: boolean
    setIsReopenCancelModalOpen: (isOpen: boolean) => void
    isError: boolean
    setIsError: (isError: boolean) => void
}

export const useHelpCenterController = (
    helpCenterRepository: HelpCenterRepository,
): IUseHelpCenterController => {
    const { t } = useTranslation()
    const [caseReasons, setCaseReasons] = useState<TicketModel[]>([])
    const [selectedReasonDetails, setSelectedReasonDetails] = useState<CaseReasonDetails[]>([])
    const [caseProducts, setCaseProducts] = useState<CaseProduct[]>([])
    const [step, setStep] = useState(1)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false)
    const [isBankAccountModalOpen, setIsBankAccountModalOpen] = useState(false)
    const [isTPVModalOpen, setIsTPVModalOpen] = useState(false)
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false)
    const [isReopenCancelModalOpen, setIsReopenCancelModalOpen] = useState(false)
    const [hasFileError, setHasFileError] = useState(false)
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
    const { startLoading, stopLoading } = useLoader()
    const { addNotification } = useNotification()
    const { merchant } = useUserData()
    const [ticketHistory, setTicketHistory] = useState<TicketHistoryItem[]>([])
    const [ticketDetail, setTicketDetail] = useState<TicketDetail | null>(null)
    const [isError, setIsError] = useState<boolean>(false)
    const navigate = useNavigate()
    const { hasPermission } = useWebTagPermissions()

    const canChangeBankAccount = hasPermission(
        WebTagPermissionEnum.ACTION_BANK_ACCOUNT_CHANGE,
    )
    const form = useForm<HelpCenterFormValues>({
        resolver: yupResolver(helpCenterFormSchema(t)),
        mode: 'onBlur',
        defaultValues: {
            caseReasonId: 0,
            caseReasonDetailId: 0,
            caseProductId: 0,
            subject: '',
            description: '',
            otherEmail: '',
            otherPhone: '',
            caseFiles: [],
            acceptPrivacyPolicy: false
        },
    })

    const reopenForm = useForm<HelpCenterReopenFormValues>({
        resolver: yupResolver(helpCenterReopenFormSchema(t)),
        mode: 'onBlur',
        defaultValues: {
            subject: '',
            description: '',
            expandedDescription: '',
            otherEmail: '',
            otherPhone: '',
            caseFiles: [],
            acceptPrivacyPolicy: false
        },
    })

    const getCaseReasons = async () => {
        try {
            const response = await helpCenterRepository.GetCaseReasons()
            const reasons = response.data as TicketModel[] ?? []
            const caseReasonsOrdered = [...reasons].sort((a, b) => a.orderToShow - b.orderToShow)
            setCaseReasons(caseReasonsOrdered)
        } catch (error) {
            console.error('Error fetching case reasons:', error)
        }
    }

    const getCaseProducts = async () => {
        try {
            startLoading()
            const response = await helpCenterRepository.GetCaseProducts()
            const products = response.data as CaseProduct[] ?? []
            const orderedProducts = [...products].sort((a, b) => a.orderToShow - b.orderToShow)
            setCaseProducts(orderedProducts)
        } catch (error) {
            console.error('Error fetching case products:', error)
        } finally {
            stopLoading()
        }
    }

    useEffect(() => {
        getCaseReasons()
    }, [])

    const caseReasonId = form.watch('caseReasonId')
    const caseReasonDetailId = form.watch('caseReasonDetailId')
    const caseProductId = form.watch('caseProductId')
    const subject = form.watch('subject')
    const description = form.watch('description')
    const acceptPrivacyPolicy = form.watch('acceptPrivacyPolicy')
    const isNursery = Boolean(merchant?.isNursery)


    const isStep4Valid =
        subject.trim().length > 0 &&
        description.trim().length > 0 &&
        acceptPrivacyPolicy &&
        !hasFileError &&
        !form.formState.errors.otherEmail &&
        !form.formState.errors.otherPhone

    const isNextEnabled =
        (step === 1 && caseReasonId > 0) ||
        (step === 2 && caseReasonDetailId > 0) ||
        (step === 3 && (caseProductId > 0 || caseProductId === -1)) ||
        (step === 4 && isStep4Valid)

    const reopenAcceptPrivacyPolicy = reopenForm.watch('acceptPrivacyPolicy')
    const reopenExpandedDescription = reopenForm.watch('expandedDescription')

    const isReopenFormValid =
        reopenExpandedDescription.trim().length > 0 &&
        reopenAcceptPrivacyPolicy &&
        !hasFileError

    const setCaseReasonDetails = () => {
        const selectedReason = caseReasons.find(r => r.caseReasonId === caseReasonId)
        const details = [...(selectedReason?.caseReasonDetails ?? [])].sort(
            (a, b) => a.orderToShow - b.orderToShow,
        )
        setSelectedReasonDetails(details)
        form.setValue('caseReasonDetailId', 0)
    }

    const saveTicketLog = async (data: any) => {
        try {
            await helpCenterRepository.SaveTicketLog(data)
        } catch (error) {
            console.error('Error saving ticket log:', error)
        }
    }

    const handleNextStep = () => {
        if (step === 1 && caseReasonId > 0) {
            setCaseReasonDetails()
            saveTicketLog({
                step: step,
                option: caseReasonId
            })
            setStep(2)
        } else if (step === 2 && caseReasonDetailId > 0) {
            if (caseReasonDetailId === 100 && !merchant.isChildWithHardDependency) {
                setIsInvoiceModalOpen(true)
                return
            }
            if (caseReasonDetailId === 106 && canChangeBankAccount) {
                setIsBankAccountModalOpen(true)
                return
            }
            if (caseReasonDetailId === 116 && !isNursery) {
                setIsTPVModalOpen(true)
                return
            }
            if (caseReasonDetailId === 111) {
                setIsMaterialModalOpen(true)
                return
            }
            saveTicketLog({
                step: step,
                option: caseReasonDetailId
            })
            setStep(3)
        } else if (step === 3 && (caseProductId > 0 || caseProductId === -1)) {
            saveTicketLog({
                step: step,
                option: caseProductId
            })
            setStep(4)
        }
    }

    const handlePreviousStep = () => {
        if (step === 3) {
            form.setValue('caseProductId', 0)
            if (caseReasonDetailId === 100 && !merchant.isChildWithHardDependency) {
                setIsInvoiceModalOpen(true)
                return
            }
            if (caseReasonDetailId === 106 && canChangeBankAccount) {
                setIsBankAccountModalOpen(true)
                return
            }
            if (caseReasonDetailId === 116 && !isNursery) {
                setIsTPVModalOpen(true)
                return
            }
            if (caseReasonDetailId === 111) {
                setIsMaterialModalOpen(true)
                return
            }
        }
        if (step > 1) setStep(step - 1)
    }

    const saveCaseRequest = async () => {
        try {
            startLoading()
            const values = form.getValues()
            const request: SaveCaseRequest = {
                caseReasonId: values.caseReasonId,
                caseReasonDetailId: values.caseReasonDetailId,
                caseProductId: values.caseProductId,
                subject: values.subject,
                description: values.description,
                otherEmail: values.otherEmail,
                otherPhone: values.otherPhone,
                caseFiles: values.caseFiles,
                acceptPrivacyPolicy: values.acceptPrivacyPolicy,
            }
            await helpCenterRepository.SaveCaseRequest(request)
            addNotification(t(HelpCenterTranslations.successMessage), NotificationSeverity.success)
        } catch (error) {
            console.error('Error saving case request:', error)
        } finally {
            stopLoading()
            navigate(navigationRoutes.myTickets)
        }
    }

    const getTicketHistory = async () => {
        try {
            startLoading()
            const response = await helpCenterRepository.GetTicketHistory()
            const tickets = response.data as TicketHistoryItem[] ?? []
            setTicketHistory(tickets)
        } catch (error) {
            console.error('Error fetching ticket history:', error)
        } finally {
            stopLoading()
        }
    }

    const getTicketDetail = async (ticketId: string) => {
        try {
            startLoading()
            const response = await helpCenterRepository.GetTicketDetail(ticketId)
            if (response.meta?.status === MetaStatusCodes.ERROR) {
                setIsError(true)
            } else {
                setTicketDetail(response.data ?? null)
            }
        } catch (error) {
            setIsError(true)
        }
        finally {
            stopLoading()
        }
    }

    const saveTicketReopen = async () => {
        try {
            startLoading()
            const values = reopenForm.getValues()
            const request: SaveCaseReopenRequest = {
                caseId: ticketDetail?.caseId.toString() ?? '',
                caseNumber: ticketDetail?.caseNumber ?? '',
                subject: values.subject,
                description: values.description,
                expandedDescription: values.expandedDescription,
                otherEmail: values.otherEmail,
                otherPhone: values.otherPhone,
                caseFiles: values.caseFiles,
                acceptPrivacyPolicy: values.acceptPrivacyPolicy,
            }
            await helpCenterRepository.SaveTicketReopen(request)
            addNotification(t(HelpCenterTranslations.successMessage), NotificationSeverity.success)
        } catch (error) {
            console.error('Error reopening ticket:', error)
        } finally {
            stopLoading()
            navigate(navigationRoutes.myTickets)
        }
    }

    const handleReopenCancel = () => {
        if (reopenForm.getValues().expandedDescription || reopenForm.getValues().caseFiles.length > 0) {
            setIsReopenCancelModalOpen(true)
        } else {
            navigate(navigationRoutes.ticketDetail.replace(':ticketId', String(ticketDetail?.caseId)))
        }
    }

    return {
        getCaseReasons,
        caseReasons,
        selectedReasonDetails,
        step,
        setStep,
        form,
        isNextEnabled,
        handleNextStep,
        handlePreviousStep,
        caseProducts,
        getCaseProducts,
        saveCaseRequest,
        isModalOpen,
        setIsModalOpen,
        setHasFileError,
        isInvoiceModalOpen,
        setIsInvoiceModalOpen,
        isBankAccountModalOpen,
        setIsBankAccountModalOpen,
        isTPVModalOpen,
        setIsTPVModalOpen,
        uploadedFiles,
        setUploadedFiles,
        ticketHistory,
        getTicketHistory,
        getTicketDetail,
        ticketDetail,
        isError,
        setIsError,
        isMaterialModalOpen,
        setIsMaterialModalOpen,
        isReopenCancelModalOpen,
        setIsReopenCancelModalOpen,
        reopenForm,
        isReopenFormValid,
        saveTicketReopen,
        handleReopenCancel
    }
}

